import {
  Badge,
  AgeingBadge,
  Button,
  CellSub,
  EmptyState,
  FilterChip,
  NativeSelect,
  PageHeader,
  Pagination,
  SearchInput,
  Skeleton,
  Table,
  TableShell,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@provenance/design';
import {
  DocumentStatus,
  IntakeChannel,
  intakeChannelLabel,
  type DocumentRow,
  type MasterDataItem,
  type Page,
} from '@provenance/shared';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileClock,
  Inbox,
  PartyPopper,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { auDate, auDateTime, country, humanise } from '../lib/format';
import { DocumentStatusBadge } from '../lib/status';
import { IntakeChannelBadge } from '../features/documents/IntakeChannelBadge';

/**
 * The document queue — Pending, Awaiting clarification, and Processed history.
 *
 * One component for all three because they are the same table with a different status
 * filter, and forking it would mean three places to fix a column.
 *
 * ── The constraints that shape this screen ──────────────────────────────────
 *
 *  SERVER-SIDE EVERYTHING. The real Processed Documents history is 190,000+ rows
 *  (spec §17). Page, sort, filter and search all go to the server. Nothing here ever
 *  holds a full result set, and there is no client-side sort helper to tempt anyone.
 *
 *  FILTER STATE LIVES IN THE URL. An operator who finds an overdue ChAFTA request and
 *  sends the link to a colleague should send the view, not the screen. It also means the
 *  browser's back button behaves the way people expect, and a refresh does not lose the
 *  filter someone just set up.
 *
 *  AGEING IS THE PRIMARY AXIS. Pending defaults to sorting by requested date ascending,
 *  because the oldest-overdue item is almost always the right next thing to open.
 * ───────────────────────────────────────────────────────────────────────────
 */

type QueueVariant = 'pending' | 'clarification' | 'processed';

const VARIANTS: Record<
  QueueVariant,
  {
    eyebrow: string;
    title: string;
    alt: string;
    lede: string;
    status: string;
    defaultSort: string;
    defaultDir: 'asc' | 'desc';
    showAgeing: boolean;
    showProcessed: boolean;
  }
> = {
  pending: {
    eyebrow: 'Pending for your action',
    title: 'Pending',
    alt: 'Oldest first.',
    lede:
      'Requests lodged by exporters and by integration partners, awaiting review. Opening one runs the completeness check automatically.',
    status: DocumentStatus.AWAITING_ACTION,
    defaultSort: 'requestedTargetDate',
    defaultDir: 'asc',
    showAgeing: true,
    showProcessed: false,
  },
  clarification: {
    eyebrow: 'Awaiting clarification',
    title: 'Awaiting clarification',
    alt: 'Not rejected — paused.',
    lede:
      'You have asked the submitter for something. These stay attached to the original request and return to the pending queue as soon as they respond.',
    status: DocumentStatus.CLARIFICATION_REQUESTED,
    defaultSort: 'receivedAt',
    defaultDir: 'desc',
    showAgeing: true,
    showProcessed: false,
  },
  processed: {
    eyebrow: 'Processed',
    title: 'Processed',
    alt: 'And everything you have not.',
    lede:
      'Every certificate issued, rejected or revoked by this chamber. Searchable by certificate number, reference, company or invoice.',
    status: `${DocumentStatus.APPROVED},${DocumentStatus.REJECTED},${DocumentStatus.REVOKED}`,
    defaultSort: 'processedAt',
    defaultDir: 'desc',
    showAgeing: false,
    showProcessed: true,
  },
};

const AGEING_OPTIONS = [
  { value: '', label: 'Any date' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Due today' },
  { value: '3days', label: 'Due within 3 days' },
  { value: 'week', label: 'Due within a week' },
  { value: 'none', label: 'No target date' },
];

export function DocumentQueue({ variant }: { variant: QueueVariant }) {
  const config = VARIANTS[variant];
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  // Search is debounced locally; everything else commits to the URL immediately.
  const [searchDraft, setSearchDraft] = useState(params.get('q') ?? '');

  useEffect(() => {
    setSearchDraft(params.get('q') ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const current = params.get('q') ?? '';
      if (searchDraft === current) return;
      const next = new URLSearchParams(params);
      if (searchDraft) next.set('q', searchDraft);
      else next.delete('q');
      next.delete('page');
      setParams(next, { replace: true });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchDraft, params, setParams]);

  const set = useCallback(
    (key: string, value: string | number | undefined) => {
      const next = new URLSearchParams(params);
      if (value === undefined || value === '' || value === null) next.delete(key);
      else next.set(key, String(value));
      // Any filter change invalidates the page number — page 7 of a narrower result set
      // is usually empty, which reads as "no results" and is confusing.
      if (key !== 'page') next.delete('page');
      setParams(next);
    },
    [params, setParams],
  );

  const page = Number(params.get('page') ?? 1);
  const pageSize = Number(params.get('pageSize') ?? 25);
  const sortBy = params.get('sortBy') ?? config.defaultSort;
  const sortDir = (params.get('sortDir') ?? config.defaultDir) as 'asc' | 'desc';
  const documentTypeId = params.get('documentTypeId') ?? '';
  const intakeChannel = params.get('intakeChannel') ?? '';
  const ageing = params.get('ageing') ?? '';
  const q = params.get('q') ?? '';
  const status = params.get('status') ?? config.status;

  const query = useQuery({
    queryKey: [
      'documents',
      { status, page, pageSize, sortBy, sortDir, documentTypeId, intakeChannel, ageing, q },
    ],
    queryFn: () =>
      api.get<Page<DocumentRow>>('/api/console/documents', {
        status,
        page,
        pageSize,
        sortBy,
        sortDir,
        documentTypeId: documentTypeId || undefined,
        intakeChannel: intakeChannel || undefined,
        ageing: ageing || undefined,
        q: q || undefined,
      }),
    // Keeps the previous page visible while the next loads, so the table does not
    // collapse to a spinner every time someone pages.
    placeholderData: (previous) => previous,
  });

  const documentTypes = useQuery({
    queryKey: ['master-data', 'document-types-for-filter'],
    queryFn: () => api.get<MasterDataItem[]>('/api/console/master-data/REJECTION_REASON'),
    // Only used to know whether master data is reachable; the type list comes from the
    // rows themselves below, which avoids a second endpoint for a filter.
    enabled: false,
  });
  void documentTypes;

  // The type filter is built from the types actually present, so a chamber never sees a
  // filter option that would return nothing.
  const typeOptions = useMemo(() => {
    const seen = new Map<string, string>();
    for (const row of query.data?.items ?? []) {
      seen.set(row.documentTypeCode, row.documentTypeName);
    }
    return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [query.data]);

  const activeFilters = [
    ageing ? { key: 'ageing', label: 'Date', value: AGEING_OPTIONS.find((o) => o.value === ageing)?.label ?? ageing } : null,
    documentTypeId ? { key: 'documentTypeId', label: 'Type', value: documentTypeId } : null,
    intakeChannel
      ? { key: 'intakeChannel', label: 'Channel', value: intakeChannelLabel(intakeChannel as IntakeChannel) }
      : null,
    variant === 'processed' && params.get('status') && params.get('status') !== config.status
      ? { key: 'status', label: 'Status', value: humanise(params.get('status')!) }
      : null,
  ].filter((f): f is { key: string; label: string; value: string } => f !== null);

  const onSort = (key: string) => {
    if (sortBy === key) {
      set('sortDir', sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      set('sortBy', key);
      set('sortDir', key === 'requestedTargetDate' ? 'asc' : 'desc');
    }
  };

  const total = query.data?.total ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={config.title}
        lede={config.lede}
      />

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={searchDraft}
          onChange={setSearchDraft}
          placeholder="Certificate number, reference, company or invoice"
          className="w-full sm:w-[24rem]"
          aria-label="Search the queue"
        />

        {config.showAgeing && (
          <label className="flex items-center gap-1.5">
            <span className="sr-only">Filter by requested date</span>
            <NativeSelect
              value={ageing}
              onChange={(e) => set('ageing', e.target.value)}
              className="h-9 w-auto min-w-[10rem]"
            >
              {AGEING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </NativeSelect>
          </label>
        )}

        {variant === 'processed' && (
          <label className="flex items-center gap-1.5">
            <span className="sr-only">Filter by outcome</span>
            <NativeSelect
              value={params.get('status') ?? ''}
              onChange={(e) => set('status', e.target.value || undefined)}
              className="h-9 w-auto min-w-[9rem]"
            >
              <option value="">Any outcome</option>
              <option value={DocumentStatus.APPROVED}>Issued</option>
              <option value={DocumentStatus.REJECTED}>Rejected</option>
              <option value={DocumentStatus.REVOKED}>Revoked</option>
            </NativeSelect>
          </label>
        )}

        {typeOptions.length > 1 && (
          <label className="flex items-center gap-1.5">
            <span className="sr-only">Filter by document type</span>
            <NativeSelect
              value={documentTypeId}
              onChange={(e) => set('documentTypeId', e.target.value || undefined)}
              className="h-9 w-auto min-w-[11rem]"
            >
              <option value="">Any document type</option>
              {typeOptions.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </NativeSelect>
          </label>
        )}

        <label className="flex items-center gap-1.5">
          <span className="sr-only">Filter by how it arrived</span>
          <NativeSelect
            value={intakeChannel}
            onChange={(e) => set('intakeChannel', e.target.value || undefined)}
            className="h-9 w-auto min-w-[10rem]"
          >
            <option value="">Any channel</option>
            <option value={IntakeChannel.INTEGRATOR}>Integrator</option>
            <option value={IntakeChannel.UPLOAD}>Upload</option>
            <option value={IntakeChannel.EMAIL}>Email</option>
          </NativeSelect>
        </label>

        <div className="flex-1" />

        {variant === 'processed' && (
          <Button
            variant="secondary"
            size="sm"
            leadingIcon={<Download className="size-3.5" />}
            onClick={() => {
              // Exports what is on screen, filters and all — the operator asked for what
              // they were looking at. Opened rather than fetched so the browser handles
              // the download and the stream is never held in memory here.
              const params = new URLSearchParams(window.location.search);
              params.set('status', status);
              window.location.href = `/api/console/documents/export?${params.toString()}`;
            }}
          >
            Export
          </Button>
        )}
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <FilterChip
              key={filter.key}
              label={filter.label}
              value={filter.value}
              onRemove={() => set(filter.key, undefined)}
            />
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setParams(new URLSearchParams(q ? { q } : {}))}
          >
            Clear all
          </Button>
        </div>
      )}

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : total === 0 ? (
        <QueueEmptyState
          variant={variant}
          filtered={Boolean(q || ageing || documentTypeId || intakeChannel)}
        />
      ) : (
        <TableShell
          footer={
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={(next) => set('page', next)}
              onPageSizeChange={(size) => {
                set('pageSize', size);
                set('page', 1);
              }}
            />
          }
        >
          <Table className={config.showProcessed ? 'min-w-[1080px]' : 'min-w-[880px]'}>
            <Thead>
              <Tr>
                <Th sortKey="senderReference" activeSort={{ key: sortBy, dir: sortDir }} onSort={onSort}>
                  Reference
                </Th>
                <Th sortKey="submittedByName" activeSort={{ key: sortBy, dir: sortDir }} onSort={onSort}>
                  Submitted by
                </Th>
                <Th>Document type</Th>
                <Th>Destination</Th>
                {config.showAgeing && (
                  <Th sortKey="requestedTargetDate" activeSort={{ key: sortBy, dir: sortDir }} onSort={onSort}>
                    Required by
                  </Th>
                )}
                <Th sortKey="receivedAt" activeSort={{ key: sortBy, dir: sortDir }} onSort={onSort}>
                  Received
                </Th>
                {/* Only once issued: on the pending queues every row would read "—" and
                    "Awaiting action", which is noise in the screen people use most. */}
                {config.showProcessed && (
                  <>
                    <Th sortKey="certificateNo" activeSort={{ key: sortBy, dir: sortDir }} onSort={onSort}>
                      Certificate no.
                    </Th>
                    <Th sortKey="processedAt" activeSort={{ key: sortBy, dir: sortDir }} onSort={onSort}>
                      Processed
                    </Th>
                    <Th>Status</Th>
                  </>
                )}
                <Th className="w-10">
                  <span className="sr-only">Checks</span>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {query.data!.items.map((row) => (
                <Tr key={row.id} onClick={() => navigate(`/documents/${row.id}`)}>
                  <Td>
                    <span className="pv-mono [font-size:12.5px] font-medium">{row.senderReference}</span>
                    {/* How it arrived, always — not only when there is an integrator.
                        An upload or an email has no integrator name, and silence in this
                        cell would be the least informative thing we could show. */}
                    <CellSub className="mt-0.5 inline-flex items-center gap-1">
                      <IntakeChannelBadge channel={row.intakeChannel} identity={row.intakeIdentity} />
                    </CellSub>
                  </Td>
                  <Td>
                    <span className="font-medium">{row.organisationName}</span>
                    {row.organisationActive === false && (
                      <Badge tone="warn" size="sm" className="ml-1.5">
                        Exporter inactive
                      </Badge>
                    )}
                    {row.supportingDocumentCount > 0 && (
                      <CellSub>
                        {row.supportingDocumentCount} attachment{row.supportingDocumentCount === 1 ? '' : 's'}
                      </CellSub>
                    )}
                  </Td>
                  <Td>
                    <span>{shortTypeName(row.documentTypeName)}</span>
                    {(row.submissionType === 'AMENDMENT' || row.messageType === 'REISSUE') && (
                      <CellSub>{row.submissionType === 'AMENDMENT' ? 'Amendment' : 'Reissue'}</CellSub>
                    )}
                  </Td>
                  <Td>{country(row.destinationCountry)}</Td>
                  {config.showAgeing && (
                    <Td>
                      <AgeingBadge daysToTarget={row.daysToTarget} />
                      {row.requestedTargetDate && (
                        <CellSub>{auDate(row.requestedTargetDate)}</CellSub>
                      )}
                    </Td>
                  )}
                  <Td>{auDate(row.receivedAt)}</Td>
                  {config.showProcessed && (
                    <>
                      <Td>
                        {row.certificateNo ? (
                          <span className="pv-mono [font-size:12.5px] font-semibold">{row.certificateNo}</span>
                        ) : (
                          <span className="text-[var(--pv-faint)]">—</span>
                        )}
                      </Td>
                      <Td>
                        {auDateTime(row.processedAt)}
                        {row.processedByName && <CellSub>{row.processedByName}</CellSub>}
                      </Td>
                      <Td>
                        <DocumentStatusBadge status={row.status} size="sm" />
                      </Td>
                    </>
                  )}
                  <Td>
                    {row.checkSummary === 'FAIL' ? (
                      <Tooltip content="The completeness check found something that looks wrong">
                        <span className="inline-flex text-[var(--pv-stop)]">
                          <AlertTriangle aria-hidden className="size-4" />
                          <span className="sr-only">Completeness check failed</span>
                        </span>
                      </Tooltip>
                    ) : row.checkSummary === 'WARNING' ? (
                      <Tooltip content="The completeness check flagged something worth a look">
                        <span className="inline-flex text-[var(--pv-warn)]">
                          <AlertTriangle aria-hidden className="size-4" />
                          <span className="sr-only">Completeness check has warnings</span>
                        </span>
                      </Tooltip>
                    ) : null}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </TableShell>
      )}
    </div>
  );
}

/**
 * Designed empty states (spec §16).
 *
 * "No pending requests" is a *good* state for a chamber, and it should read like one
 * rather than like a broken screen. A filtered empty result is a different situation and
 * gets a different message — the fix is to widen the filter, not to wait.
 */
function QueueEmptyState({ variant, filtered }: { variant: QueueVariant; filtered: boolean }) {
  const [, setParams] = useSearchParams();

  if (filtered) {
    return (
      <EmptyState
        icon={<Inbox className="size-5" />}
        title="Nothing matches those filters"
        description="Try widening the date range, clearing the document type, or searching for a different reference."
        action={
          <Button variant="secondary" onClick={() => setParams(new URLSearchParams())}>
            Clear the filters
          </Button>
        }
      />
    );
  }

  if (variant === 'pending') {
    return (
      <EmptyState
        tone="good"
        icon={<PartyPopper className="size-5" />}
        title="The queue is clear"
        description="Nothing is waiting on you. New requests arrive here from exporters and from integration partners, and you will be emailed when they do."
      />
    );
  }

  if (variant === 'clarification') {
    return (
      <EmptyState
        tone="good"
        icon={<FileClock className="size-5" />}
        title="Nobody is holding anything up"
        description="When you ask a submitter for more information, the request waits here until they respond — then it returns to the pending queue."
      />
    );
  }

  return (
    <EmptyState
      icon={<CheckCircle2 className="size-5" />}
      title="No certificates issued yet"
      description="Once you approve or reject a request it appears here, permanently, with who actioned it and when."
    />
  );
}

/** "AANZFTA Certificate of Origin" reads as "AANZFTA" in a column that already says what it is. */
function shortTypeName(name: string): string {
  return name.replace(/\s+(Certificate|Declaration) of (Australian )?Origin$/i, '').trim() || name;
}
